import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Row,
  Col,
  Card,
  Tag,
  Button,
  Select,
  Space,
  Divider,
  Spin,
  Alert,
  Empty,
  message,
  InputNumber,
  Rate,
} from 'antd';
import {
  ShoppingCartOutlined,
  LeftOutlined,
  EnvironmentOutlined,
  CheckCircleOutlined,
  StarOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useStores } from '../../stores';
import ReviewList from '../../components/reviews/ReviewList';
import FavoriteButton from '../../components/favorites/FavoriteButton';
import InquiryModal from '../../components/inquiry/InquiryModal';
import './ProductDetailPage.scss';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const ProductDetailPage = observer(() => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { productStore, cartStore, authStore } = useStores();

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [inquiryModalVisible, setInquiryModalVisible] = useState(false);

  useEffect(() => {
    // Fetch product details when component mounts
    const fetchProduct = async () => {
      const result = await productStore.fetchProductById(id);

      if (result.success && result.data) {
        // Auto-select first variant and inventory if available
        if (result.data.variants && result.data.variants.length > 0) {
          const firstVariant = result.data.variants[0];
          setSelectedVariant(firstVariant);

          if (firstVariant.partner_inventories && firstVariant.partner_inventories.length > 0) {
            setSelectedInventory(firstVariant.partner_inventories[0]);
          }
        }
      }
    };

    fetchProduct();

    // Cleanup on unmount
    return () => {
      productStore.setSelectedProduct(null);
    };
  }, [id, productStore]);

  const product = productStore.selectedProduct;

  const handleVariantChange = (variantId) => {
    const variant = product.variants.find(v => v.id === variantId);
    setSelectedVariant(variant);

    // Auto-select first inventory for this variant
    if (variant.partner_inventories && variant.partner_inventories.length > 0) {
      setSelectedInventory(variant.partner_inventories[0]);
    } else {
      setSelectedInventory(null);
    }
  };

  const handleSellerChange = (inventoryId) => {
    const inventory = selectedVariant.partner_inventories.find(inv => inv.id === inventoryId);
    setSelectedInventory(inventory);
  };

  const getVariantDisplay = (variant) => {
    const parts = [];
    if (variant.color) parts.push(variant.color);
    if (variant.size) parts.push(variant.size);
    if (variant.weight) parts.push(`${variant.weight}kg`);
    return parts.join(' / ') || 'Standard';
  };

  const handleAddToCart = async () => {
    // Check if user is logged in
    if (!authStore.isAuthenticated) {
      message.warning(t('products.detail.loginRequired'));
      navigate('/login');
      return;
    }

    // Validate selections
    if (!selectedVariant || !selectedInventory) {
      message.error(t('products.detail.selectVariantAndSeller'));
      return;
    }

    // Validate quantity
    if (quantity < 1 || quantity > selectedInventory.stock_quantity) {
      message.error(t('products.detail.invalidQuantity', { max: selectedInventory.stock_quantity }));
      return;
    }

    setAddingToCart(true);

    const result = await cartStore.addToCart(
      selectedVariant.id,
      selectedInventory.id,
      quantity
    );

    setAddingToCart(false);

    if (result.success) {
      message.success(t('products.detail.addedToCart', { quantity }));
      setQuantity(1); // Reset quantity
    } else {
      message.error(result.error || t('products.detail.addFailed'));
    }
  };

  if (productStore.loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip={t('products.detail.loading')} />
      </div>
    );
  }

  if (productStore.error || !product) {
    return (
      <div className="sweet-container" style={{ padding: '48px 24px' }}>
        <Alert
          message={t('common.error')}
          description={productStore.error || t('products.detail.notFound')}
          type="error"
          showIcon
        />
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate('/products')}
          style={{ marginTop: 16 }}
        >
          {t('products.detail.backToProducts')}
        </Button>
      </div>
    );
  }

  const title = product.title || 'Product';
  const description = product.description || '';
  const hasVariants = product.variants && product.variants.length > 0;

  // Get image URL based on available sizes
  const getImageUrl = (imageField) => {
    if (!imageField) return null;

    // VersatileImageField returns an object with sizes
    if (typeof imageField === 'object') {
      return imageField.large || imageField.full_size || imageField.medium;
    }

    // Fallback for old format (direct URL string)
    return imageField;
  };

  const mainImageUrl = getImageUrl(product.main_image);

  return (
    <div className="product-detail-page">
      <div className="sweet-container">
        <Button
          icon={<LeftOutlined />}
          onClick={() => navigate('/products')}
          className="back-button"
        >
          {t('products.detail.backToProducts')}
        </Button>

        <Row gutter={[32, 32]} style={{ marginTop: 24 }}>
          {/* Product Image */}
          <Col xs={24} md={10}>
            <Card className="product-image-card">
              {mainImageUrl ? (
                <img src={mainImageUrl} alt={title} className="product-detail-image" />
              ) : (
                <div className="product-image-placeholder">
                  <ShoppingCartOutlined />
                </div>
              )}
            </Card>
          </Col>

          {/* Product Details */}
          <Col xs={24} md={14}>
            <div className="product-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                <Title level={1} className="product-title" style={{ marginBottom: 0 }}>
                  {title}
                </Title>
                <FavoriteButton
                  type="product"
                  itemId={parseInt(id)}
                  showText
                  size="large"
                />
              </div>

              {product.category && (
                <Tag color="purple" className="category-tag">
                  {i18n.language === 'el' ? (product.category.name_el || product.category.name) : product.category.name}
                </Tag>
              )}

              {/* Rating Display */}
              {product.average_rating > 0 && (
                <div className="product-rating" style={{ margin: '12px 0' }}>
                  <Rate disabled allowHalf value={parseFloat(product.average_rating)} />
                  <Text style={{ marginLeft: 8 }}>
                    {parseFloat(product.average_rating).toFixed(1)} ({t('products.detail.reviews', { count: product.rating_count })})
                  </Text>
                </div>
              )}

              {description && (
                <div
                  className="product-description"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}

              <Divider />

              {hasVariants ? (
                <>
                  {/* Variant Selection */}
                  <div className="selection-section">
                    <label className="selection-label">{t('products.detail.selectVariant')}</label>
                    <Select
                      value={selectedVariant?.id}
                      onChange={handleVariantChange}
                      style={{ width: '100%' }}
                      size="large"
                    >
                      {product.variants.map(variant => (
                        <Option key={variant.id} value={variant.id}>
                          {getVariantDisplay(variant)}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  {/* Seller Selection */}
                  {selectedVariant && selectedVariant.partner_inventories && selectedVariant.partner_inventories.length > 0 ? (
                    <div className="selection-section">
                      <label className="selection-label">{t('products.detail.selectSeller')}</label>
                      <Select
                        value={selectedInventory?.id}
                        onChange={handleSellerChange}
                        style={{ width: '100%' }}
                        size="large"
                      >
                        {selectedVariant.partner_inventories.map(inv => (
                          <Option key={inv.id} value={inv.id}>
                            <Space>
                              <EnvironmentOutlined />
                              <span>{inv.partner_name || `${t('products.detail.seller')} ${inv.partner_id}`}</span>
                              <span>-</span>
                              <Text strong>€{parseFloat(inv.price).toFixed(2)}</Text>
                              {inv.stock_quantity > 0 && (
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                              )}
                            </Space>
                          </Option>
                        ))}
                      </Select>

                      {/* Selected Inventory Details */}
                      {selectedInventory && (
                        <Card className="inventory-details-card">
                          {/* Seller Info with Link - Similar to Services */}
                          <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f9f9f9', borderRadius: 8 }}>
                            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                              <EnvironmentOutlined style={{ marginRight: 4 }} />
                              {t('products.detail.soldBy')}
                            </Text>
                            <div>
                              <a
                                onClick={() => navigate(`/partners/${selectedInventory.partner_id}`)}
                                style={{ fontWeight: 'bold', cursor: 'pointer', color: '#9FB18C', fontSize: 18 }}
                              >
                                {selectedInventory.partner_name || `${t('products.detail.seller')} ${selectedInventory.partner_id}`}
                              </a>
                            </div>
                            {selectedInventory.partner_address && (
                              <div style={{ marginTop: 8, color: '#666', fontSize: 14 }}>
                                {selectedInventory.partner_address.address && (
                                  <div>{selectedInventory.partner_address.address}</div>
                                )}
                                {(selectedInventory.partner_address.city || selectedInventory.partner_address.postal_code) && (
                                  <div>
                                    {selectedInventory.partner_address.city}
                                    {selectedInventory.partner_address.postal_code && `, ${selectedInventory.partner_address.postal_code}`}
                                  </div>
                                )}
                              </div>
                            )}
                            <Button
                              type="link"
                              style={{ padding: 0, marginTop: 8, color: '#9FB18C' }}
                              onClick={() => navigate(`/partners/${selectedInventory.partner_id}`)}
                            >
                              {t('products.detail.viewPartnerProfile')} →
                            </Button>
                          </div>

                          <Divider style={{ margin: '12px 0' }} />

                          <Row gutter={16}>
                            <Col span={12}>
                              <div className="detail-item">
                                <Text type="secondary">{t('common.price')}</Text>
                                <Title level={2} className="price">
                                  €{parseFloat(selectedInventory.price).toFixed(2)}
                                </Title>
                              </div>
                            </Col>
                            <Col span={12}>
                              <div className="detail-item">
                                <Text type="secondary">{t('products.detail.shippingCost')}</Text>
                                <Title level={4} className="shipping">
                                  €{parseFloat(selectedInventory.shipping_cost || 0).toFixed(2)}
                                </Title>
                              </div>
                            </Col>
                          </Row>

                          <Divider />

                          <Space direction="vertical" style={{ width: '100%' }}>
                            <div className="stock-info">
                              {selectedInventory.stock_quantity > 0 ? (
                                <>
                                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                  <Text strong style={{ color: '#52c41a' }}>
                                    {t('products.detail.inStock', { count: selectedInventory.stock_quantity })}
                                  </Text>
                                </>
                              ) : (
                                <Text type="danger" strong>
                                  {t('products.detail.outOfStock')}
                                </Text>
                              )}
                            </div>

                            {selectedInventory.stock_quantity > 0 && (
                              <div className="quantity-selector">
                                <Text strong>{t('common.quantity')}:</Text>
                                <InputNumber
                                  min={1}
                                  max={selectedInventory.stock_quantity}
                                  value={quantity}
                                  onChange={setQuantity}
                                  size="large"
                                  style={{ width: '100%', marginTop: 8 }}
                                />
                              </div>
                            )}

                            <Button
                              type="primary"
                              size="large"
                              icon={<ShoppingCartOutlined />}
                              disabled={selectedInventory.stock_quantity === 0}
                              loading={addingToCart}
                              onClick={handleAddToCart}
                              block
                            >
                              {authStore.isAuthenticated ? t('products.detail.addToCart') : t('products.detail.loginToAdd')}
                            </Button>

                            <Button
                              size="large"
                              icon={<QuestionCircleOutlined />}
                              onClick={() => setInquiryModalVisible(true)}
                              block
                            >
                              {t('products.detail.askQuestion')}
                            </Button>
                          </Space>
                        </Card>
                      )}
                    </div>
                  ) : (
                    <Empty
                      description={t('products.detail.noSellers')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )}
                </>
              ) : (
                <Empty
                  description={t('products.detail.noVariants')}
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </Col>
        </Row>

        {/* Reviews Section */}
        <Divider />
        <div className="reviews-section" style={{ marginTop: 32 }}>
          <Title level={3}>
            <StarOutlined /> {t('products.detail.customerReviews')}
          </Title>
          <ReviewList type="product" itemId={id} />
        </div>
      </div>

      {/* Inquiry Modal */}
      <InquiryModal
        visible={inquiryModalVisible}
        onClose={() => setInquiryModalVisible(false)}
        inquiryType="PRODUCT"
        productId={parseInt(id)}
        productName={title}
        partnerId={selectedInventory?.partner_id}
        partnerName={selectedInventory?.partner_name}
      />
    </div>
  );
});

export default ProductDetailPage;
